import React from 'react';
import { Box, Typography, IconButton, Chip, Tooltip } from '@mui/material';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { useCases } from '../../contexts/api-contexts/CasesContext';

interface CaseSummarySummaryProps {
  isCollapsed?: boolean;
  isActive?: boolean;
}

export const CaseSummarySummary: React.FC<CaseSummarySummaryProps> = ({
  isCollapsed = false,
  isActive = false,
}) => {
  const { currentCase } = useCases();
  const backgroundColor = isActive ? 'rgba(0, 150, 136, 0.12)' : 'transparent';

  // Count completed sections
  const completedSections = React.useMemo(() => {
    let count = 0;
    if (currentCase?.patient) count++;
    if (
      currentCase?.selected_symptoms &&
      currentCase.selected_symptoms.length > 0
    )
      count++;
    if (
      currentCase?.selected_diagnoses &&
      currentCase.selected_diagnoses.length > 0
    )
      count++;
    if (
      currentCase?.selected_treatments &&
      currentCase.selected_treatments.length > 0
    )
      count++;
    return count;
  }, [
    currentCase?.patient,
    currentCase?.selected_symptoms,
    currentCase?.selected_diagnoses,
    currentCase?.selected_treatments,
  ]);

  const getSummaryText = (): { text: string; count: string | null } => {
    if (completedSections === 0) {
      return {
        text: 'Complete the case steps to view summary',
        count: null,
      };
    }

    if (completedSections === 4) {
      return {
        text: 'Case complete - ready to review',
        count: '✓',
      };
    }

    return {
      text: `sections completed`,
      count: `${completedSections}/4`,
    };
  };

  // Collapsed view - just show icon
  if (isCollapsed) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1.5,
          backgroundColor,
          borderLeft: isActive ? '3px solid #009688' : 'none',
        }}
      >
        <Tooltip
          title={
            completedSections > 0
              ? `${completedSections}/4 sections completed`
              : 'Summary'
          }
        >
          <IconButton size="small" color={isActive ? 'primary' : 'default'}>
            <SummarizeIcon />
          </IconButton>
        </Tooltip>
        {completedSections > 0 && (
          <Chip
            label={completedSections === 4 ? '✓' : `${completedSections}/4`}
            size="small"
            color={completedSections === 4 ? 'success' : 'primary'}
            sx={{ mt: 0.5, height: 18, fontSize: '0.7rem' }}
          />
        )}
      </Box>
    );
  }

  // Expanded view
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor,
        borderLeft: isActive ? '3px solid #009688' : 'none',
      }}
    >
      <Box display="flex" alignItems="center" mb={1}>
        <SummarizeIcon
          color={isActive ? 'primary' : 'inherit'}
          sx={{ mr: 1 }}
        />
        <Typography
          variant="subtitle1"
          color={isActive ? 'primary' : 'inherit'}
          sx={{ flexGrow: 1, textAlign: 'left' }}
        >
          Summary
        </Typography>
        {completedSections > 0 && (
          <Chip
            label={completedSections === 4 ? '✓' : `${completedSections}/4`}
            size="small"
            color={completedSections === 4 ? 'success' : 'primary'}
          />
        )}
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        component="div"
        sx={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}
      >
        {getSummaryText().count &&
          getSummaryText().count !== '✓' &&
          getSummaryText().count !== `${completedSections}/4` && (
            <Chip
              label={getSummaryText().count}
              sx={{
                bgcolor: '#00c853',
                color: 'white',
                fontWeight: 'bold',
                mr: 1,
                '&:hover': {
                  bgcolor: '#00a045',
                  cursor: 'pointer',
                },
              }}
              size="small"
            />
          )}
        {getSummaryText().text}
      </Typography>
    </Box>
  );
};

export default CaseSummarySummary;
